// src/pages/artist/ArtistDashboard.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import contractService from '../../services/contractService';
import artworkService from '../../services/artworkService';
import walletService from '../../services/walletService';
import { extractApiList } from '../../utils/responseAdapters';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircleCheck, faClock, faBan, faFileContract, faTriangleExclamation, faWallet,
} from '@fortawesome/free-solid-svg-icons';
import '../../styles/ArtistDashboard.css';

// ─── Contract Status Card ─────────────────────────────────────────────────────

function ContractStatusCard() {
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchContract = useCallback(async () => {
    try {
      const res = await contractService.getMyContractsArtist();
      const list = extractApiList(res?.data);
      const active = list.find(c => c.status === 'active');
      const pending = list.find(c => c.status === 'pending');
      setContract(active || pending || null);
    } catch {
      setContract(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchContract(); }, [fetchContract]);

  if (loading) return null;

  const days = contract?.status === 'active' && contract.end_date
    ? Math.ceil((new Date(contract.end_date) - new Date()) / 86400000)
    : null;
  const expiringSOon = days !== null && days <= 30;

  if (!contract) {
    return (
      <div style={{
        background: '#fff3cd', border: '1px solid #ffc107', borderRadius: 12,
        padding: '16px 20px', marginBottom: 24, display: 'flex',
        alignItems: 'center', gap: 14,
      }}>
        <FontAwesomeIcon icon={faFileContract} style={{ fontSize: 22, color: '#856404' }} />
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, color: '#856404' }}>No Active Manager</div>
          <div style={{ color: '#856404', fontSize: 13 }}>
            You need an active management contract to apply for TitTat tokenization.
          </div>
        </div>
        <Link
          to="/artist/dashboard/contracts"
          style={{
            background: '#ffc107', color: '#000', padding: '8px 16px',
            borderRadius: 8, textDecoration: 'none', fontWeight: 600, fontSize: 13,
          }}
        >
          Request a Manager →
        </Link>
      </div>
    );
  }

  if (contract.status === 'pending') {
    return (
      <div style={{
        background: '#e8f4fd', border: '1px solid #2d8cf0', borderRadius: 12,
        padding: '16px 20px', marginBottom: 24, display: 'flex',
        alignItems: 'center', gap: 14,
      }}>
        <FontAwesomeIcon icon={faClock} style={{ fontSize: 22, color: '#2d8cf0' }} />
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, color: '#2d8cf0' }}>Contract Pending Approval</div>
          <div style={{ color: '#555', fontSize: 13 }}>
            Awaiting approval from <strong>{contract.manager_info?.name || contract.admin_id}</strong>.
            Check your contracts page for details.
          </div>
        </div>
        <Link
          to="/artist/dashboard/contracts"
          style={{
            background: '#2d8cf0', color: '#fff', padding: '8px 16px',
            borderRadius: 8, textDecoration: 'none', fontWeight: 600, fontSize: 13,
          }}
        >
          Manage →
        </Link>
      </div>
    );
  }

  // Active contract
  return (
    <div style={{
      background: expiringSOon ? '#fffbeb' : '#ecfdf5',
      border: `1px solid ${expiringSOon ? '#f59e0b' : '#10b981'}`,
      borderRadius: 12, padding: '16px 20px', marginBottom: 24,
      display: 'flex', alignItems: 'center', gap: 14,
    }}>
      <FontAwesomeIcon
        icon={expiringSOon ? faTriangleExclamation : faCircleCheck}
        style={{ fontSize: 22, color: expiringSOon ? '#f59e0b' : '#10b981' }}
      />
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 700, color: expiringSOon ? '#92400e' : '#065f46' }}>
          Managed by {contract.manager_info?.name || contract.admin_id}
        </div>
        <div style={{ color: '#555', fontSize: 13 }}>
          Contract active until{' '}
          <strong>
            {new Date(contract.end_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
          </strong>
          {expiringSOon && <span style={{ color: '#f59e0b', fontWeight: 600 }}> — Expiring in {days} day{days !== 1 ? 's' : ''}</span>}
        </div>
      </div>
      <Link
        to="/artist/dashboard/contracts"
        style={{
          background: expiringSOon ? '#f59e0b' : '#10b981',
          color: '#fff', padding: '8px 16px',
          borderRadius: 8, textDecoration: 'none', fontWeight: 600, fontSize: 13,
        }}
      >
        {expiringSOon ? 'Renew Early →' : 'View Contract →'}
      </Link>
    </div>
  );
}

// ─── Dashboard ────────────────────────────────────────────────────────────────

const ArtistDashboard = () => {
  const [stats, setStats] = useState(null);
  const [balance, setBalance] = useState(null);

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const [statsRes, balRes] = await Promise.allSettled([
          artworkService.getArtistDashboardStats(),
          walletService.getBalance(),
        ]);
        if (!active) return;
        if (statsRes.status === 'fulfilled') setStats(statsRes.value?.data?.data || null);
        if (balRes.status === 'fulfilled') setBalance(balRes.value?.data?.data || null);
      } catch {
        if (active) setStats(null);
      }
    };
    load();
    return () => { active = false; };
  }, []);

  const artsByStatus = stats?.arts_by_status || {};
  const totalArts = Object.values(artsByStatus).reduce((sum, n) => sum + (n || 0), 0);

  return (
    <DashboardLayout>
      <div style={{ padding: '24px 20px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 20 }}>
          <div className="application-card">
            <div style={{ fontSize: 12, color: '#777' }}>Total Artworks</div>
            <div style={{ fontSize: 24, fontWeight: 700 }}>{totalArts}</div>
          </div>
          <div className="application-card">
            <div style={{ fontSize: 12, color: '#777' }}>Pending Tokenization</div>
            <div style={{ fontSize: 24, fontWeight: 700 }}>{stats?.pending_tokenization_applications || 0}</div>
          </div>
          <div className="application-card">
            <div style={{ fontSize: 12, color: '#777' }}>Tokenized Assets</div>
            <div style={{ fontSize: 24, fontWeight: 700 }}>{stats?.tokenized_assets_count || 0}</div>
          </div>
          <div className="application-card">
            <div style={{ fontSize: 12, color: '#777' }}>Active Contract</div>
            <div style={{ fontSize: 24, fontWeight: 700 }}>{stats?.has_active_contract ? 'Yes' : 'No'}</div>
          </div>
          {balance && (
            <Link to="/artist/dashboard/wallet" style={{ textDecoration: 'none', color: 'inherit' }}>
              <div className="application-card" style={{ background: 'linear-gradient(135deg, #1e293b, #334155)', color: '#fff', cursor: 'pointer' }}>
                <div style={{ fontSize: 12, color: '#94a3b8' }}><FontAwesomeIcon icon={faWallet} style={{ marginRight: 4 }} />Wallet</div>
                <div style={{ fontSize: 24, fontWeight: 700 }}>
                  ${Number(balance.balance || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </div>
              </div>
            </Link>
          )}
        </div>
        <ContractStatusCard />
      </div>
    </DashboardLayout>
  );
};

export default ArtistDashboard;